// TODO: When appointment config / admin dashboard are refactored into React,
// Use this component for the no more showings' cancel appointment modal
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Timezone } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { AppointmentController } from 'api';
import {
  formatDateStrNoOrd,
  formatDateStrWithYearShort,
  formatTimeStr,
} from 'utils';
import {
  AppointmentTypes,
  VirtualTableData,
} from '../../../Router/pages/ShowingsCalendarPage/types';
import message from '../../Message';
import { Icon } from '../../Icon';
import { InputTextArea } from '../../Input';
import { Modal } from '../../Modal';
import { Table } from '../../Table';
import { Text } from '../../Typography';
import theme from '../../theme';

interface CancelAppointmentsModalProps {
  address?: string;
  listingId: string;
  // Make sure to sort appointments by `time Desc` from BE
  appointments: Array<
    Exclude<AppointmentTypes[number], VirtualTableData[number]>
  >;
  closeModal: () => void;
  refetchPage?: () => Promise<void>;
  modalTitle?: string;
}

const CancelAppointmentsModal: React.FC<CancelAppointmentsModalProps> = ({
  address,
  listingId,
  appointments,
  closeModal,
  refetchPage,
  modalTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedAppointments, setSelectedAppointments] = useState(
    appointments.reduce<Record<string, boolean>>((acc, appointment) => {
      acc[appointment.id] = true;
      return acc;
    }, {}),
  );
  const [cancelReason, setCancelReason] = useState('');

  const onCancelAppointments = async () => {
    setLoading(true);
    try {
      await AppointmentController.cancelMultipleAppointments(
        listingId,
        selectedAppointments,
        cancelReason,
      );
      setLoading(false);
      closeModal();
      message.success('Showings cancelled');
      if (refetchPage) await refetchPage();
    } catch (err) {
      errorReporter.error(err);
      message.error('Error cancelling remaining showings');
      setLoading(false);
    }
  };

  let modalTitleText = modalTitle || 'Cancel Remaining Showings';
  if (address) modalTitleText = `${address} - ${modalTitleText}`;

  const dataSource = useMemo(() => appointments.map(appointment => ({
      ...appointment,
      key: appointment.id,
      dateStr: formatDateStrNoOrd(
        appointment.time,
        appointment.listing.timezone,
      ),
      fullTimeStr: `${formatTimeStr(
        appointment.time,
        appointment.listing.timezone,
      )} - ${formatTimeStr(appointment.timeEnd, appointment.listing.timezone)}`,
      coop: { ...appointment.coop, type: appointment.type },
    })), [appointments]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'dateStr',
    },
    {
      title: 'Time',
      dataIndex: 'fullTimeStr',
    },
    {
      title: 'Showing Agent',
      dataIndex: 'coop',
      render: (coop: { name: string; org: { name: string }; type: string }) => (
        <>
          {coop.name}
          <div>
            <Text strong>{coop.org.name}</Text>
          </div>
          <StyledCoopType
            externalCoop={[
              'Buyer Visit',
              'Appraisal',
              'Home Inspection',
              'Photographer',
              'Pest Control',
            ].includes(coop.type)}
          >
            {coop.type}
          </StyledCoopType>
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'legacyStatus',
      width: 250,
      render: (
        legacyStatus: { note: string; time: string },
        record: { listing: { timezone: Timezone } },
      ) => (
        <>
          {legacyStatus.note}
          <br />
          {formatDateStrWithYearShort(legacyStatus.time)}{' '}
          {formatTimeStr(legacyStatus.time, record.listing.timezone)}
        </>
      ),
    },
  ];

  const onRowSelect = (selectedRowKeys: string[]) => {
    const newSelection = selectedRowKeys.reduce<Record<string, boolean>>(
      (acc, curr) => {
        acc[curr] = true;
        return acc;
      },
      {},
    );
    setSelectedAppointments(newSelection);
  };

  const DetailsRequired = () => (
    <DetailsRequiredContainer>
      <Icon
        type="antdWarningFilled"
        color="errorColor"
        width={12}
        height={12}
      />
      Details required
    </DetailsRequiredContainer>
  );

  return (
    <Modal
      open={Boolean(appointments?.length)}
      title={modalTitleText}
      maskClosable={false}
      cancelText="Do Not Cancel"
      onCancel={closeModal}
      okText={`Cancel (${Object.keys(selectedAppointments).length}) Showings`}
      onOk={onCancelAppointments}
      okButtonProps={{
        loading,
        disabled:
          Object.keys(selectedAppointments).length === 0 ||
          !cancelReason.length,
      }}
      width="max(500px, 60vw)"
      bodyStyle={{
        maxHeight: '70vh',
        marginBottom: 12,
      }}
    >
      <Table
        pagination={false}
        columns={columns}
        dataSource={dataSource}
        rowSelection={{
          defaultSelectedRowKeys: Object.keys(selectedAppointments),
          onChange: onRowSelect,
        }}
        style={{
          border: '1px solid #e9e9e9',
          height: `calc(70vh - 150px)`,
          overflow: 'auto',
        }}
      />
      <ReasonContainer>
        Reason for Cancelling:
        {!cancelReason && <DetailsRequired />}
      </ReasonContainer>
      <InputTextArea
        placeholder="Why are you cancelling these showings?"
        rows={2}
        onChange={e => setCancelReason(e.target.value)}
        data-cy="cancel-reason-textarea"
      />
    </Modal>
  );
};

export default CancelAppointmentsModal;

const StyledCoopType = styled.div<{ externalCoop: boolean }>`
  font-size: 11px;
  ${({ externalCoop }) => (externalCoop ? `color: ${theme.red6}` : '')}
`;

const ReasonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
`;

const DetailsRequiredContainer = styled.div`
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 12px;
  color: ${theme.red5};
  gap: 8px;
`;
